import React, { useState, useEffect } from "react";
import { FiUserPlus, FiMail, FiUser, FiCalendar, FiCheck, FiX } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import Modal from "@/components/modal";
import Pagination from "@/components/pagination";
import Loader from "@/components/loader";
import api from "@/services/api";
import useStore from "@/services/store";

export default function Users() {
  const { t } = useComponentTranslation(translations);
  const { organization } = useStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const perPage = 50;

  useEffect(() => {
    fetchUsers();
  }, [currentPage, organization]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const organizationId = organization._id;

      const response = await api.post("/user/search", {
        limit: perPage,
        organization_id: organizationId,
        role_recruiter: true,
      });

      if (!response.ok) throw response;
      setUsers(response.data || []);
      setTotal(response.total || 0);
    } catch (e) {
      console.error("Error fetching users:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteSuccess = () => {
    fetchUsers();
    setIsInviteModalOpen(false);
  };

  return (
    <div className="w-full p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
            <p className="text-sm text-gray-600 mt-1">{t("subtitle")}</p>
          </div>
          <button onClick={() => setIsInviteModalOpen(true)} className="btn btn-primary flex items-center gap-2">
            <FiUserPlus className="w-4 h-4" />
            {t("invite_button")}
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <FiUser className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-900 font-medium mb-1">{t("no_users")}</p>
            <p className="text-sm text-gray-500 mb-4">{t("no_users_description")}</p>
            <button onClick={() => setIsInviteModalOpen(true)} className="btn btn-primary">
              {t("invite_button")}
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3">{t("name")}</th>
                    <th className="px-6 py-3">{t("email")}</th>
                    <th className="px-6 py-3">{t("status")}</th>
                    <th className="px-6 py-3">{t("created_at")}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => {
                    const userId = user._id;
                    const userName = user.name || `${user.firstname || ""} ${user.lastname || ""}`.trim() || user.email;
                    const isInvited = !user.registered_at;
                    const isActive = user.registered_at;

                    return (
                      <tr key={userId} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelectedUser(user)}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <FiUser className="w-4 h-4 text-primary" />
                            </div>
                            <span className="text-sm font-medium text-gray-900">{userName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <FiMail className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{user.email || "-"}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isInvited ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <FiMail className="w-3 h-3" />
                              {t("invited")}
                            </span>
                          ) : isActive ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <FiCheck className="w-3 h-3" />
                              {t("active")}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <FiX className="w-3 h-3" />
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FiCalendar className="w-4 h-4 text-gray-400" />
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > perPage && (
              <div className="border-t border-gray-200 px-6 py-4">
                <Pagination total={total} limit={perPage} currentPage={currentPage} onPageChange={setCurrentPage} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Invite Modal */}
      <InviteModal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} onSuccess={handleInviteSuccess} />

      {/* View Modal */}
      <ViewModal user={selectedUser} isOpen={!!selectedUser} onClose={() => setSelectedUser(null)} />
    </div>
  );
}

function InviteModal({ isOpen, onClose, onSuccess }) {
  const { t } = useComponentTranslation(translations);
  const { organization } = useStore();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (value) => {
    setEmail(value);
    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!email.trim()) {
      setError(t("email_required"));
      return;
    }
    if (!validateEmail(email)) {
      setError(t("email_invalid"));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/user/invite", {
        email: email.trim().toLowerCase(),
        organization_id: organization?._id,
        organization_name: organization?.name,
        role_recruiter: true,
      });

      if (!response.ok) throw response;
      toast.success(t("invite_success"));
      setEmail("");
      setError("");
      onSuccess();
    } catch (e) {
      console.error("Error sending invitation:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="w-full max-w-md">
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t("invite_modal_title")}</h2>
          <p className="text-sm text-gray-600">{t("invite_modal_description")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t("email_label")} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={t("email_placeholder")}
              className={`input w-full ${error ? "border-red-500" : ""}`}
              disabled={loading}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={handleClose} className="btn btn-secondary" disabled={loading}>
              {t("cancel_button")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading || !email.trim()}>
              {loading ? t("loading") : t("invite_button_submit")}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

function ViewModal({ user, isOpen, onClose }) {
  const { t } = useComponentTranslation(translations);
  const [reinviteLoading, setReinviteLoading] = useState(false);

  if (!user) return null;

  const userName = user.name || `${user.firstname || ""} ${user.lastname || ""}`.trim() || user.email;
  const isInvited = !user.registered_at;
  const isActive = user.active !== false && user.registered_at;

  const handleReinvite = async () => {
    try {
      setReinviteLoading(true);
      const response = await api.post("/user/reinvite", {
        user_id: user._id,
      });

      if (!response.ok) throw response;
      toast.success(t("reinvite_success"));
    } catch (e) {
      console.error("Error reinviting user:", e);
      toast.error(e.code);
    } finally {
      setReinviteLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-2xl">
      <div className="p-6">
        <div className="space-y-4">
          {/* User Avatar/Info */}
          <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <FiUser className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{userName}</h3>
              <p className="text-sm text-gray-600">{user.email}</p>
            </div>
          </div>

          {/* User Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("name")}</label>
              <p className="text-sm text-gray-900  px-3 py-2 rounded-lg">{userName || "-"}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("email")}</label>
              <p className="text-sm text-gray-900  px-3 py-2 rounded-lg">{user.email || "-"}</p>
            </div>

            {user.job_title && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("job_title")}</label>
                <p className="text-sm text-gray-900  px-3 py-2 rounded-lg">{user.job_title}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("status")}</label>
              <div className="mt-1">
                {isInvited ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <FiMail className="w-3 h-3" />
                    {t("invited")}
                  </span>
                ) : isActive ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <FiCheck className="w-3 h-3" />
                    {t("active")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <FiX className="w-3 h-3" />
                    {t("inactive")}
                  </span>
                )}
              </div>
            </div>

            {user.phone && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("phone")}</label>
                <p className="text-sm text-gray-900 px-3 py-2 rounded-lg">{user.phone}</p>
              </div>
            )}

            {user.organisations?.[0]?.name && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("organization")}</label>
                <p className="text-sm text-gray-900 px-3 py-2 rounded-lg">{user.organisations[0].name}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("created_at")}</label>
              <p className="text-sm text-gray-900 px-3 py-2 rounded-lg">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}</p>
            </div>

            {user.registered_at && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("registered_at")}</label>
                <p className="text-sm text-gray-900 px-3 py-2 rounded-lg">{new Date(user.registered_at).toLocaleDateString()}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200 mt-4">
            <div>
              {isInvited && (
                <button onClick={handleReinvite} disabled={reinviteLoading} className="btn btn-primary flex items-center gap-2">
                  <FiMail className="w-4 h-4" />
                  {reinviteLoading ? t("loading") : t("reinvite_button")}
                </button>
              )}
            </div>
            <button onClick={onClose} className="btn btn-secondary">
              {t("close_button")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const translations = {
  en: {
    title: "Members",
    subtitle: "Manage members in your organization",
    invite_button: "Invite Member",
    invite_modal_title: "Invite Member",
    invite_modal_description: "Send an invitation email to a new member to join your organization",
    name_label: "Name",
    name_placeholder: "Enter full name",
    email_label: "Email",
    email_placeholder: "Enter email address",
    invite_button_submit: "Send Invitation",
    cancel_button: "Cancel",
    fetch_error: "Failed to fetch members",
    invite_success: "Invitation sent successfully",
    invite_error: "Failed to send invitation",
    email_required: "Email is required",
    name_required: "Name is required",
    email_invalid: "Please enter a valid email address",
    email_exists: "This email is already registered",
    loading: "Loading...",
    no_users: "No members found",
    no_users_description: "Invite members to get started",
    name: "Name",
    email: "Email",
    job_title: "Job Title",
    status: "Status",
    invited: "Invited",
    active: "Active",
    inactive: "Inactive",
    created_at: "Joined",
    view_modal_title: "Member Details",
    view_modal_description: "View detailed information about this member",
    phone: "Phone",
    organization: "Organization",
    registered_at: "Registered",
    reinvite_button: "Resend Invitation",
    reinvite_success: "Invitation resent successfully",
    reinvite_error: "Failed to resend invitation",
    close_button: "Close",
  },
  fr: {
    title: "Membres",
    subtitle: "Gérez les membres de votre organisation",
    invite_button: "Inviter un membre",
    invite_modal_title: "Inviter un membre",
    invite_modal_description: "Envoyez un email d'invitation à un nouveau membre pour rejoindre votre organisation",
    name_label: "Nom",
    name_placeholder: "Entrez le nom complet",
    email_label: "Email",
    email_placeholder: "Entrez l'adresse email",
    invite_button_submit: "Envoyer l'invitation",
    cancel_button: "Annuler",
    fetch_error: "Échec de la récupération des membres",
    invite_success: "Invitation envoyée avec succès",
    invite_error: "Échec de l'envoi de l'invitation",
    email_required: "L'email est requis",
    name_required: "Le nom est requis",
    email_invalid: "Veuillez entrer une adresse email valide",
    email_exists: "Cet email est déjà enregistré",
    loading: "Chargement...",
    no_users: "Aucun membre trouvé",
    no_users_description: "Invitez des membres pour commencer",
    name: "Nom",
    email: "Email",
    job_title: "Titre du poste",
    status: "Statut",
    invited: "Invité",
    active: "Actif",
    inactive: "Inactif",
    created_at: "Rejoint",
    view_modal_title: "Détails du membre",
    view_modal_description: "Voir les informations détaillées sur ce membre",
    phone: "Téléphone",
    organization: "Organisation",
    registered_at: "Inscrit",
    reinvite_button: "Renvoyer l'invitation",
    reinvite_success: "Invitation renvoyée avec succès",
    reinvite_error: "Échec du renvoi de l'invitation",
    close_button: "Fermer",
  },
};
